import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  Skeleton,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import * as _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { url as commonUrl } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { UploadReceivablesCSVForm } from './UploadReceivablesCSVForm';
import { DeleteIconButton } from 'packages/common-frontend/components/IconButton';
import {
  GreenCircleCheck,
  RedCircleCross,
} from 'packages/common-frontend/components/icons';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCreditFacilitiesAndLoanProductsForAssignmentGroupingQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useLatestAssignmentPipelineRunMetadataForImplementationContext } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForImplementationProvider';
import { PipelineStatusAlert } from 'packages/loanProduct/ui/v2/PipelineStatusAlert';
import {
  FiFilePlus,
  FiMinusCircle,
  FiUpload,
  FiDownload,
  FiChevronLeft,
} from 'react-icons/fi';
import { z } from 'zod/v4';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

type ReceivableError = { message: string; testReceivableId: string };

type ReceivableUpload = {
  loanProductId: string;
  loanProductName: string;
  creditFacilityId: string;
  creditFacilityName: string;
  key: string;
  isValid: boolean | undefined; // undefined indicates either no file has been added or that file hasn't been validated yet
  fileError: string;
  errors: ReceivableError[];
  isLoading: boolean;
  file: File | null;
};

const initialUploadState = {
  isValid: undefined,
  errors: [],
  file: null,
  fileError: '',
};

// Using loanProductId-creditFacilityId as key
type LoanProductCreditFacilityReceivableUploadMap = {
  [key: string]: ReceivableUpload;
};

const getStatusIcon = (
  isValid: boolean | undefined,
  isFileUploaded: boolean,
) => {
  if (!isFileUploaded) {
    return <FiMinusCircle />;
  }
  if (isFileUploaded && isValid === undefined) {
    return <FiFilePlus />;
  }
  if (isValid) {
    return <GreenCircleCheck />;
  }
  return <RedCircleCross />;
};

export const LoanProductCreditFacilityReceivableUpload = ({
  receivableUpload,
  setModalReceivableUpload,
  setReceivableUploadsMap,
  isGeneratingPreview,
}: {
  receivableUpload: ReceivableUpload;
  setModalReceivableUpload: (receivableUpload: ReceivableUpload) => void;
  setReceivableUploadsMap: React.Dispatch<
    React.SetStateAction<LoanProductCreditFacilityReceivableUploadMap>
  >;
  isGeneratingPreview: boolean;
}) => {
  const statusIcon = getStatusIcon(
    receivableUpload.isValid,
    !!receivableUpload.file,
  );

  const downloadCSVErrors = (filename: string, errors: ReceivableError[]) => {
    const csvRows = [];
    const headers = ['Error', 'Receivable Id'];
    csvRows.push(headers.join(','));
    errors.forEach((error) => {
      csvRows.push([error.message, error.testReceivableId].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const tag = document.createElement('a');
    tag.setAttribute('href', url);
    tag.setAttribute(
      'download',
      `${filename.substring(0, filename.length - 4)}-errors.csv`,
    );
    tag.click();
  };

  return (
    <Flex justify="flex-start" mt="18px" mb="18px" alignItems="center">
      <Flex
        justifyContent="center"
        flexWrap="wrap"
        ml="15px"
        mr="15px"
        width="20px"
      >
        {statusIcon}
      </Flex>
      <Text fontSize="lg" mr="40px" width="600px">
        {receivableUpload.loanProductName} &{' '}
        {receivableUpload.creditFacilityName}
      </Text>
      {receivableUpload.isValid === false ? (
        <Flex direction="column" width="400px" mr="40px">
          {receivableUpload.fileError ? (
            <Text fontSize="lg" color="red">
              {receivableUpload.fileError}
            </Text>
          ) : (
            <>
              <Text fontSize="lg" mb="10px" color="red">
                Some of the proposed receivables are invalid. Fix errors and try
                again.
              </Text>
              <Button
                size="sm"
                aria-label="download csv errors"
                rightIcon={<FiDownload />}
                width="fit-content"
                onClick={() => {
                  if (receivableUpload.file) {
                    downloadCSVErrors(
                      receivableUpload.file.name,
                      receivableUpload.errors,
                    );
                  }
                }}
              >
                Download Errors
              </Button>
            </>
          )}
        </Flex>
      ) : (
        <Text fontSize="lg" mr="40px" width="400px">
          {receivableUpload.file
            ? receivableUpload.file.name
            : 'No File uploaded'}
        </Text>
      )}

      <Button
        colorScheme={receivableUpload.isValid === false ? 'red' : 'green'}
        aria-label="upload receivables by csv"
        isLoading={receivableUpload.isLoading}
        onClick={() => {
          setModalReceivableUpload(receivableUpload);
        }}
        disabled={isGeneratingPreview}
        variant="outline"
        rightIcon={<FiUpload />}
      >
        {receivableUpload.file ? 'Re-upload ' : 'Upload '}File
      </Button>
      <Flex ml="10px" mr="10px">
        <DeleteIconButton
          ariaLabel="remove receivables csv"
          label="Remove Receivables CSV"
          onClick={() => {
            setReceivableUploadsMap((receivableUploadsMap) => {
              return {
                ...receivableUploadsMap,
                [receivableUpload.key]: {
                  ...receivableUploadsMap[receivableUpload.key],
                  isLoading: false,
                  ...initialUploadState,
                },
              };
            });
          }}
          fontSize="2xl"
          colorScheme="red"
          isDisabled={!receivableUpload.file || isGeneratingPreview}
        />
      </Flex>
    </Flex>
  );
};

export const AssignmentEditByCSVBody = ({
  implementationId,
  masterLoanTapeIds,
  fileVersion,
  reportingDate,
}: {
  implementationId: string;
  masterLoanTapeIds: string[];
  fileVersion: number;
  reportingDate: Util.FullDateStr;
}) => {
  const { accessToken } = useUserAuthOrThrow();
  const [
    receivableUploadsMap,
    setReceivableUploadsMap,
  ] = useState<LoanProductCreditFacilityReceivableUploadMap>({});

  const [
    showGeneratePreviewAlert,
    setShowGeneratePreviewAlert,
  ] = useState<boolean>(false);

  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);

  const [isGeneratingPreview, setIsGeneratingPreview] = useState<boolean>(
    false,
  );

  const [
    modalReceivableUpload,
    setModalReceivableUpload,
  ] = useState<ReceivableUpload>();
  const onClose = () => {
    setModalReceivableUpload(undefined);
  };

  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    setShowGeneratePreviewAlert(false);
  }, [receivableUploadsMap]);

  const generatePreview = async (): Promise<void> => {
    const formData = new FormData();
    const loanProductCreditFacilityPairs: {
      loanProductId: string;
      creditFacilityId: string;
    }[] = [];

    Object.values(receivableUploadsMap).forEach((upload) => {
      if (upload.isValid === undefined && upload.file) {
        // isValid === undefined indicates either no file has been added or that file hasn't been validated yet
        loanProductCreditFacilityPairs.push({
          loanProductId: upload.loanProductId,
          creditFacilityId: upload.creditFacilityId,
        });
        formData.append(
          `${upload.loanProductId}-${upload.creditFacilityId}-file`,
          upload.file,
        );
      }
    });

    const url = commonUrl.getBorrowerExpressUrl(
      commonUrl.borrowerExpressPaths.assignByCSVPreviewGenAndCSVUpload,
    );
    formData.append('implementationId', implementationId);
    formData.append(
      'loanProductCreditFacilityPairs',
      JSON.stringify(loanProductCreditFacilityPairs),
    );
    formData.append('masterLoanTapeIds', JSON.stringify(masterLoanTapeIds));
    formData.append('fileVersion', String(fileVersion));
    formData.append('reportingDate', reportingDate);

    try {
      const { data } = await axios.post(url, formData, {
        timeout: 300000, // 5 minute timeout
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${accessToken.raw}`,
        },
      });

      const newReceivableUploadsMap = { ...receivableUploadsMap };
      data.uploadResults.forEach(
        (upload: {
          key: string;
          isValid: boolean;
          errors: ReceivableError[];
        }) => {
          const updatedReceivableUpload = {
            ...receivableUploadsMap[upload.key],
            isValid: upload.isValid,
            errors: upload.errors,
          };
          newReceivableUploadsMap[upload.key] = updatedReceivableUpload;
        },
      );
      setReceivableUploadsMap(newReceivableUploadsMap);
      if (data.areCSVsValid) {
        setShowSuccessAlert(true);
        setTimeout(() => {
          setShowSuccessAlert(false);
          navigate(
            `/assignments/${implementationId}/summary?previewJobId=${data.assignmentPreviewJobId}`,
          );
        }, 4000);
      }
    } catch (err: any) {
      Sentry.captureException(JSON.stringify(err, null, 2));
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error generating preview and saving receivables file. Please try again, and contact your Finley representative if this persists.',
      });
    }
  };

  const {
    data: creditFacilitiesForAssignmentGroupingData,
    loading: creditFacilitiesForAssignmentGroupingDataLoading,
  } = useGetCreditFacilitiesAndLoanProductsForAssignmentGroupingQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      implementationId,
    },
    onCompleted(data) {
      const receivableUploadMap: LoanProductCreditFacilityReceivableUploadMap = {};

      data.creditFacilitiesForAssignmentGrouping.forEach((creditFacility) => {
        creditFacility.loanProducts.forEach((loanProduct) => {
          receivableUploadMap[loanProduct.id + creditFacility.id] = {
            loanProductId: loanProduct.id,
            loanProductName: loanProduct.name,
            creditFacilityId: creditFacility.id,
            creditFacilityName: creditFacility.name,
            key: loanProduct.id + creditFacility.id,
            isLoading: false,
            ...initialUploadState,
          };
        });
      });

      setReceivableUploadsMap(receivableUploadMap);
    },
    fetchPolicy: 'network-only',
  });

  if (
    !creditFacilitiesForAssignmentGroupingData ||
    !creditFacilitiesForAssignmentGroupingData.creditFacilitiesForAssignmentGrouping ||
    creditFacilitiesForAssignmentGroupingDataLoading
  ) {
    return <Skeleton />;
  }

  // Perform basic file structure validation on each file addition, checking that the file is single column.
  // Then all receivable validation is performed when user clicks Preview Proposed Owners.
  // If some files are invalid, those errors are displayed. If all files are valid, then preview is generated.

  const validateFileStructure = async (
    file: File,
    receivableUpload: ReceivableUpload | undefined,
  ): Promise<void> => {
    if (!receivableUpload) return;
    // Reset error state and display loading icon
    setReceivableUploadsMap({
      ...receivableUploadsMap,
      [receivableUpload.key]: {
        ...receivableUploadsMap[receivableUpload.key],
        isLoading: true,
        ...initialUploadState,
      },
    });

    const url = commonUrl.getBorrowerExpressUrl(
      commonUrl.borrowerExpressPaths.assignmentCSVValidation,
    );
    const formData = new FormData();
    formData.append('loanProductId', receivableUpload.loanProductId);
    formData.append('creditFacilityId', receivableUpload.creditFacilityId);
    formData.append('receivables', file);
    formData.append('assignmentType', 'assignByCSV');

    try {
      const { data } = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${accessToken.raw}`,
        },
      });

      const fileError = data.isValid ? '' : data.fileErrors[0];

      const updatedReceivableUpload = {
        ...receivableUploadsMap[receivableUpload.key],
        isValid: data.isValid === false ? false : undefined,
        isLoading: false,
        fileError,
        file,
      };
      setReceivableUploadsMap({
        ...receivableUploadsMap,
        [updatedReceivableUpload.key]: updatedReceivableUpload,
      });
    } catch (err: any) {
      Sentry.captureException(JSON.stringify(err, null, 2));
      toast({
        ...errorToastConfig,
        id: undefined, // Avoid merging timeouts if multiple errors.
        title:
          'Error processing file. Please try again, and contact your Finley representative if this persists.',
      });
      const updatedReceivableUpload = {
        ...receivableUploadsMap[receivableUpload.key],
        // Stop the spinner.
        isLoading: false,
      };
      setReceivableUploadsMap({
        ...receivableUploadsMap,
        [updatedReceivableUpload.key]: updatedReceivableUpload,
      });
    } finally {
      setModalReceivableUpload(undefined);
    }
  };

  return (
    <>
      {_.map(receivableUploadsMap, (receivableUpload) => {
        return (
          <React.Fragment key={receivableUpload.key}>
            <LoanProductCreditFacilityReceivableUpload
              receivableUpload={receivableUpload}
              setModalReceivableUpload={setModalReceivableUpload}
              setReceivableUploadsMap={setReceivableUploadsMap}
              isGeneratingPreview={isGeneratingPreview}
            />
            <hr />
          </React.Fragment>
        );
      })}
      <Flex direction="column" mt="150px" justifyContent="flex-end">
        {showGeneratePreviewAlert && (
          <Alert status="error" mb="10px">
            <AlertIcon />
            <AlertDescription>
              Please upload files or fix errors to generate a new preview.
            </AlertDescription>
          </Alert>
        )}
        {isGeneratingPreview && (
          <Alert status="warning" mb="10px">
            <AlertIcon />
            <AlertDescription>Verifying file uploads.</AlertDescription>
          </Alert>
        )}
        {showSuccessAlert && (
          <Alert status="success" mb="10px">
            <AlertIcon />
            <AlertDescription>
              Successfully verified and uploaded files. You will be redirected
              to the summary page shortly.
            </AlertDescription>
          </Alert>
        )}
        <hr />
        <Flex justifyContent="space-between" mt="10px">
          <Button
            leftIcon={<FiChevronLeft />}
            variant="ghost"
            onClick={() => {
              navigate(`/assignments/${implementationId}/summary`);
            }}
          >
            Back
          </Button>
          <Button
            colorScheme="aurora"
            onClick={async () => {
              try {
                const validUploadExists = Object.values(
                  receivableUploadsMap,
                ).find((upload) => upload.file && upload.isValid === undefined);
                if (!validUploadExists) {
                  setShowGeneratePreviewAlert(true);
                } else {
                  setIsGeneratingPreview(true);
                  await generatePreview();
                  setIsGeneratingPreview(false);
                }
              } catch (err: any) {
                if (!toast.isActive(errorToastId)) {
                  errorToastConfig.title = `Error previewing assignment`;
                  toast(errorToastConfig);
                }
                throw err;
              }
            }}
            isLoading={isGeneratingPreview}
            disabled={isGeneratingPreview}
          >
            Preview Proposed Owners
          </Button>
        </Flex>
      </Flex>
      <Modal
        isOpen={Boolean(modalReceivableUpload)}
        onClose={onClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent padding="20px" width="auto" minW="550px">
          <ModalHeader>
            {modalReceivableUpload &&
              `${modalReceivableUpload.loanProductName} & ${modalReceivableUpload.creditFacilityName}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UploadReceivablesCSVForm
              onClose={onClose}
              handleFile={(file) =>
                validateFileStructure(file, modalReceivableUpload)
              }
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

// Types
const RouteParamsStruct = z.object({
  implementationId: z.string({ message: 'Missing URL parameter' }),
});

export const AssignmentEditByCSVContainer = () => {
  const params = useParams();
  const { implementationId } = RouteParamsStruct.parse(params);

  const {
    metadata,
  } = useLatestAssignmentPipelineRunMetadataForImplementationContext();

  return (
    <Box height="100%">
      {(() => {
        if (metadata === undefined) {
          return <Skeleton w="100%" h="400px" />;
        }
        if (!metadata || metadata.status !== 'success') {
          return (
            <PipelineStatusAlert
              status={metadata ? metadata.status : 'noPipelineRun'}
            />
          );
        }
        return (
          <AssignmentEditByCSVBody
            implementationId={implementationId}
            masterLoanTapeIds={metadata.masterLoanTapeIds}
            fileVersion={metadata.fileVersion}
            reportingDate={metadata.reportingDate}
          />
        );
      })()}
    </Box>
  );
};
